import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_3adc9213-dab6-4851-9c35-a0c85e7e3dfd/artifacts/gaem0znj_Shoozix%20Signature.png";

const NAV_LINKS = [
  { label: "House", href: "#house" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Atelier", href: "#atelier" },
  { label: "Collections", href: "#collections" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-black/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#top"
          onClick={(e) => handleAnchor(e, "#top")}
          data-testid="navbar-logo"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Shoozix International LLP"
            className="h-12 md:h-14 w-auto"
            style={{ mixBlendMode: "multiply", filter: "contrast(1.1) brightness(1.02)" }}
          />
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="link-underline text-[13px] tracking-[0.18em] uppercase text-[#0F1B3D] font-medium"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="navbar-client-login-btn"
            className="hidden md:inline-flex group items-center gap-2 px-5 py-3 bg-[#0F1B3D] text-[#F9F8F6] text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-[#B8945F] transition-colors duration-500"
          >
            <span>Client Portal</span>
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:rotate-45" />
          </Link>
          <button
            type="button"
            aria-label="Toggle navigation"
            data-testid="navbar-mobile-toggle"
            className="lg:hidden p-2 text-[#0F1B3D]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-[#F9F8F6] border-t border-black/5"
        >
          <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchor(e, link.href)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="text-2xl font-serif text-[#0F1B3D]"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/login"
              data-testid="mobile-client-login-btn"
              className="inline-flex items-center justify-between px-5 py-4 bg-[#0F1B3D] text-[#F9F8F6] text-xs tracking-[0.2em] uppercase mt-4"
            >
              <span>Client Portal</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
